/**
 * KID_TRIVIA — the assessment surface (rank-earning mechanic).
 * Matches the operator's mockup: "ASSESSMENT MODE" header, question, four
 * geometric A/B/C/D panels (≥64px). One tap only. The correct index is known
 * only AFTER the server grades it (`correctIndex` arrives via the answer POST),
 * so styling/feedback wait for that. correct → "CORRECT. LOGGED."; wrong →
 * dim-orange "NOTED. THE ANSWER WAS [X]." (never red — a miss is not an error).
 */
import Glyph from "./Glyph";

const LETTERS = ["A", "B", "C", "D"];

export default function Trivia({
  question,
  options,
  selected,
  correctIndex,
  onPick,
}: {
  question: string;
  options: string[];
  selected: number | null;
  correctIndex: number | null; // null until the server grades the answer
  onPick: (index: number) => void;
}) {
  const answered = selected !== null;
  const graded = correctIndex !== null;
  const wasCorrect = graded && selected === correctIndex;

  return (
    <div className="kid-trivia" role="group" aria-label="Trivia challenge">
      <div className="kid-trivia__header">ASSESSMENT MODE</div>
      <p className="kid-trivia__q">{question}</p>

      <div className="kid-trivia__panels">
        {options.slice(0, 4).map((opt, i) => {
          const classes = ["kid-trivia__panel"];
          if (graded && i === correctIndex && selected === correctIndex) classes.push("is-correct");
          else if (graded && i === selected) classes.push("is-wrong");
          else if (!graded && i === selected) classes.push("is-selected");
          return (
            <button
              key={i}
              type="button"
              className={classes.join(" ")}
              disabled={answered}
              onClick={() => onPick(i)}
            >
              <span className="kid-trivia__letter">{LETTERS[i]}</span>
              <span className="kid-trivia__opt">{opt}</span>
            </button>
          );
        })}
      </div>

      {answered && !graded && (
        <div className="kid-trivia__result is-grading">ANALYZING…</div>
      )}
      {graded && (
        <div className={`kid-trivia__result ${wasCorrect ? "is-correct" : "is-wrong"}`}>
          {wasCorrect ? "CORRECT. LOGGED." : "NOTED. RECALIBRATION SCHEDULED — WE WILL REVISIT THIS ONE."}
        </div>
      )}

      <div className="kid-trivia__glyph">
        <Glyph mode="idle" size={40} />
      </div>
    </div>
  );
}
